import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";

const enum fields {
    showSeparator = "showSeparator",
    addColumn = "addColumn",
}

interface OwnProps extends WidgetProps {
    extendedStyles?: SignInWrapperStyles;
    fields: {
        [fields.showSeparator]: boolean;
        [fields.addColumn]: boolean;
    };
}

export interface SignInWrapperStyles {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    widgetsContainer?: GridContainerProps;
    twoColumnsGridItem?: GridItemProps;
    threeColumnsGridItem?: GridItemProps;
    separator?: InjectableCss;
    separatorWrapper?: InjectableCss;
    verticalSeparator?: InjectableCss;
    separatorText?: TypographyProps;
}

export const signInWrapperStyles: SignInWrapperStyles = {
    titleContainer: {
        css: css`
            border-bottom: 2px solid ${getColor("common.border")};

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: ["30px", null, "65px", "85px", null],
                            properties: ["margin-bottom"],
                        },
                        {
                            values: ["1em", null, "1.5em", null, null],
                            properties: ["padding-bottom"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    widgetsContainer: {
        gap: 0,
        css: css`
            border: 1px solid ${getColor("common.border")};

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["2em", "3em", "4em", "5em", null], properties: ["padding"] }],
                    mediaOptions.min,
                )}

            ${GridOffset} {
                justify-content: space-between;
            }
        `,
    },
    twoColumnsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            padding: 0;
        `,
    },
    threeColumnsGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-grow: 2 !important;
            max-width: inherit !important;
            padding: 0;
        `,
    },
    separator: {
        css: css`
            padding-bottom: 0;
            display: flex;
            justify-content: center;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["100%", null, null, "auto", null], properties: ["width"] }],
                    mediaOptions.min,
                )}
        `,
    },
    separatorWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["100%", null, null, "inherit", null], properties: ["width"] },
                        { values: ["inherit", null, null, "100%", null], properties: ["height"] },
                        { values: ["row", null, null, "column", null], properties: ["flex-direction"] },
                        { values: ["30px 0", null, null, "0 20px", null], properties: ["margin"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    verticalSeparator: {
        css: css`
            flex-grow: 1;
            background-color: ${getColor("common.borderLighter")};

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["100%", null, null, "2px", null], properties: ["width"] },
                        { values: ["2px", null, null, "100%", null], properties: ["height"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    separatorText: {
        size: TYPOGRAPHY.size.base,
        transform: "uppercase",
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            color: ${getColor("text.lighter")};
            padding: 20px;
        `,
    },
};

const StoreSignInWrapper: FC<OwnProps> = ({ id, extendedStyles, fields }) => {
    const [styles] = React.useState(() => mergeToNew(signInWrapperStyles, extendedStyles));

    const renderSeparator = () => {
        if (!fields.showSeparator) {
            return null;
        }

        return (
            <StyledWrapper {...styles.separatorWrapper}>
                <StyledWrapper {...styles.verticalSeparator}></StyledWrapper>
                <Typography {...styles.separatorText}>{translate("or")}</Typography>
                <StyledWrapper {...styles.verticalSeparator}></StyledWrapper>
            </StyledWrapper>
        );
    };

    const columnStyle = fields.addColumn ? styles.threeColumnsGridItem : styles.twoColumnsGridItem;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.titleContainer}>
                <Zone contentId={id} zoneName="Content0" />
            </StyledWrapper>
            <GridContainer {...styles.widgetsContainer}>
                <GridItem {...columnStyle}>
                    <Zone contentId={id} zoneName="Content1" />
                </GridItem>
                <StyledWrapper {...styles.separator}>{renderSeparator()}</StyledWrapper>
                <GridItem {...columnStyle}>
                    <Zone contentId={id} zoneName="Content2" />
                </GridItem>
                {fields.addColumn && (
                    <>
                        <StyledWrapper {...styles.separator}>{renderSeparator()}</StyledWrapper>
                        <GridItem {...columnStyle}>
                            <Zone contentId={id} zoneName="Content3" />
                        </GridItem>
                    </>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const storeSignInWrapper: WidgetModule = {
    component: StoreSignInWrapper,
    definition: {
        displayName: "Sign In Wrapper",
        group: "Sign In",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.showSeparator,
                displayName: "Show separator",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.addColumn,
                displayName: "Add a 3rd column",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default storeSignInWrapper;
